import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Video as VideoIcon, BookHeart, PlayCircle, PawPrint } from "lucide-react";
import { api } from "@/lib/api";
import { COMMUNITY_SECTION } from "@/constants/testIds";

export const CommunitySection = ({ onOpenVideo, onOpenStory }) => {
  const [videos, setVideos] = useState([]);
  const [stories, setStories] = useState([]);
  const [activeVideo, setActiveVideo] = useState(null);

  const refresh = () => {
    api.get("/community-videos").then((r) => setVideos(r.data)).catch(() => setVideos([]));
    api.get("/stories").then((r) => setStories(r.data)).catch(() => setStories([]));
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <section
      id="community"
      data-testid={COMMUNITY_SECTION.section}
      className="py-24 md:py-32 px-6 md:px-10 max-w-7xl mx-auto"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mb-14 max-w-2xl"
      >
        <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#D97736] mb-4">
          From Our Kennel Family
        </p>
        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0F2520]">
          Share Your Dog's Video &amp; Story
        </h2>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-6 mb-14">
        <motion.button
          data-testid={COMMUNITY_SECTION.uploadVideoBtn}
          onClick={onOpenVideo}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-left bg-[#0F2520] text-white rounded-[2rem] p-8 hover:scale-[1.02] transition-transform duration-300"
        >
          <VideoIcon className="w-8 h-8 text-[#F4D06F] mb-4" />
          <h3 className="font-display text-2xl font-semibold mb-2">Upload Your Dog's Video</h3>
          <p className="font-light text-white/70">Got a puppy from us? Share a video of your dog with our community.</p>
        </motion.button>

        <motion.button
          data-testid={COMMUNITY_SECTION.writeStoryBtn}
          onClick={onOpenStory}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-left bg-[#E5ECD9] text-[#0F2520] rounded-[2rem] p-8 hover:scale-[1.02] transition-transform duration-300 border border-[#DCD7CE]"
        >
          <BookHeart className="w-8 h-8 text-[#D97736] mb-4" />
          <h3 className="font-display text-2xl font-semibold mb-2">Write Your Dog's Story</h3>
          <p className="font-light text-[#0F2520]/70">Tell us about your journey with your Raijada Golden Retriever.</p>
        </motion.button>
      </div>

      {videos.length > 0 && (
        <div className="mb-14">
          <h4 className="font-display text-xl font-semibold text-[#0F2520] mb-6">Community Videos</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {videos.map((v, i) => (
              <motion.div
                key={v.id}
                data-testid={COMMUNITY_SECTION.videoCard(v.id)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="relative rounded-[1.5rem] overflow-hidden shadow-lg bg-[#0F2520]"
              >
                {activeVideo === v.id ? (
                  <video
                    src={`${api.defaults.baseURL}/community-videos/${v.id}/stream`}
                    controls
                    autoPlay
                    className="w-full aspect-video bg-black"
                  />
                ) : (
                  <button
                    onClick={() => setActiveVideo(v.id)}
                    className="relative w-full aspect-video flex items-center justify-center group bg-[#16332C]"
                  >
                    <PawPrint className="w-10 h-10 text-white/20 absolute" />
                    <PlayCircle className="w-14 h-14 text-white relative z-10 group-hover:scale-110 transition-transform" />
                  </button>
                )}
                <div className="px-4 py-3">
                  <p className="text-white font-medium text-sm">{v.pet_name}</p>
                  <p className="text-white/50 text-xs">Shared by {v.owner_name}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {stories.length > 0 && (
        <div>
          <h4 className="font-display text-xl font-semibold text-[#0F2520] mb-6">Dog Stories</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stories.map((s, i) => (
              <motion.div
                key={s.id}
                data-testid={COMMUNITY_SECTION.storyCard(s.id)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-white rounded-[1.5rem] p-6 border border-[#DCD7CE]"
              >
                <p className="text-[#0F2520]/80 font-light leading-relaxed mb-4 line-clamp-5">"{s.story}"</p>
                <p className="font-display font-semibold text-[#0F2520]">
                  — {s.owner_name}{s.pet_name ? ` & ${s.pet_name}` : ""}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
